import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Button, Spinner} from "reactstrap";
import {
  STATUS_FULL_WIDTH_BUTTON_STYLE,
  VERIFY_CARD_BULLET_STYLE,
  VERIFY_CARD_HEADER_STYLE,
  VERIFY_CARD_ICON_STYLE,
  VERIFY_CARD_LIST_ITEM_STYLE,
  VERIFY_CARD_LIST_STYLE,
  VERIFY_CARD_STYLE,
  VERIFY_CARD_SUBTITLE_STYLE,
  VERIFY_CARD_TITLE_STYLE,
} from "./statusCardStyles.js";

type Props = {
  /**
   * Message affiché sous le CTA, si non vide — soit l'échec du géocodage
   * initial (« Adresse introuvable, vérifiez la saisie »), soit celui du
   * géocodage de secours de la commune tenté par « Positionner sur la
   * carte » en échec total (#980). Vide sur une précision non exploitable
   * mais RÉSOLUE (`municipality`/`unknown`, ou `toponym`/`locality` sans
   * composants structurés exploitables — #1082) — le géocodage a réussi,
   * rien à signaler en plus de la carte checklist.
   */
  error: string;
  /** Résolution du CTA en cours (échec total, géocodage de secours de la commune — #980) : désactive le bouton, affiche le spinner. */
  resolvingMapCenter: boolean;
  onPositionOnMap: () => void;
};

/**
 * État `status: "not-found"` (#799, taxonomie finale #980, lieux-dits #1082) :
 * couvre à la fois 0 résultat IGN et une précision non exploitable
 * (`municipality`/`unknown`, ou `locality`/`toponym` sans composants
 * structurés) — un seul rendu pour les deux causes, le visiteur n'a de toute
 * façon qu'une seule sortie (mock-up validé, cadre 6).
 * Carte checklist reprise telle quelle de l'ancienne machine à états du
 * tunnel (`AddressStep.tsx`/`.scss`, pré-#980) — CTA renommé « Positionner
 * sur la carte » (wording unifié, même libellé que le callout « Adresse
 * proche trouvée »).
 */
export function AddressNotFound({error, resolvingMapCenter, onPositionOnMap}: Props) {
  return (
    <>
      <div style={VERIFY_CARD_STYLE} role="alert">
        <div style={VERIFY_CARD_HEADER_STYLE}>
          <span style={VERIFY_CARD_ICON_STYLE} aria-hidden="true">
            <FontAwesomeIcon icon="map-marker-alt" />
          </span>
          <div>
            <div style={VERIFY_CARD_TITLE_STYLE}>Adresse introuvable</div>
            <div style={VERIFY_CARD_SUBTITLE_STYLE}>Vérifiez ce que vous avez saisi</div>
          </div>
        </div>
        <ul style={VERIFY_CARD_LIST_STYLE}>
          <li style={VERIFY_CARD_LIST_ITEM_STYLE}>
            <span style={VERIFY_CARD_BULLET_STYLE} aria-hidden="true">
              <FontAwesomeIcon icon="check" />
            </span>
            le numéro et la rue
          </li>
          <li style={VERIFY_CARD_LIST_ITEM_STYLE}>
            <span style={VERIFY_CARD_BULLET_STYLE} aria-hidden="true">
              <FontAwesomeIcon icon="check" />
            </span>
            le code postal
          </li>
          <li style={VERIFY_CARD_LIST_ITEM_STYLE}>
            <span style={VERIFY_CARD_BULLET_STYLE} aria-hidden="true">
              <FontAwesomeIcon icon="check" />
            </span>
            la ville
          </li>
        </ul>
      </div>
      <Button
        type="button"
        color="primary"
        outline
        size="sm"
        style={STATUS_FULL_WIDTH_BUTTON_STYLE}
        disabled={resolvingMapCenter}
        onClick={onPositionOnMap}
      >
        {resolvingMapCenter ? <Spinner size="sm" /> : "Positionner sur la carte"}
      </Button>
      {error && <div style={{fontSize: "12px", color: "#6c757d", marginTop: "6px"}}>{error}</div>}
    </>
  );
}
